import { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckCircle,
    faTimesCircle,
    faChevronLeft,
    faChevronRight,
    faMapMarkerAlt,
    faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { ClientService } from "../../component/services/client.service";
import { makeParams } from "../../component/api/makeRequest";
import { DateRange } from "../../component/DateRangeSelector/DateRangeSelector";
import { useTheme } from "../../theme/useTheme";
import moment from "moment";

interface Student {
    _id: string;
    name: string;
    rollNo: string;
    city: string;
    vendorName: string;
    facialStatus: string;
    fingerprintStatus: string;
    avatar: string | null;
    examDayPhoto: string | null;
    reverificationPhoto: string | null;
}

interface Props {
    show: boolean;
    onHide: () => void;
    vendorName: string;
    failureType: string;
    dateRange: DateRange;
}

const FAILURE_TYPE_COLORS: Record<string, string> = {
    "Facial Failed":      "#3b82f6",
    "Fingerprint Failed": "#f59e0b",
    "Both Failed":        "#ef4444",
    "Other":              "#9ca3af",
};

function StatusPill({ status }: { status: string }) {
    const matched = status === "matched";
    return (
        <span
            className="d-flex align-items-center gap-1"
            style={{ color: matched ? "#10b981" : "#ef4444", fontSize: "0.78rem", fontWeight: 500 }}
        >
            <FontAwesomeIcon icon={matched ? faCheckCircle : faTimesCircle} />
            {matched ? "Matched" : "Not Matched"}
        </span>
    );
}

function PhotoCell({ src, label, onClick }: { src: string | null; label: string; onClick?: () => void }) {
    const { colors } = useTheme();
    return (
        <div className="d-flex flex-column align-items-center gap-1">
            {src ? (
                <img
                    src={src}
                    alt={label}
                    onClick={onClick}
                    style={{
                        width: 52,
                        height: 52,
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: `1px solid ${colors.border.light}`,
                        background: colors.background.secondary,
                        cursor: "zoom-in",
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
            ) : (
                <div
                    style={{
                        width: 52, height: 52, borderRadius: "8px",
                        background: colors.background.secondary,
                        border: `1px solid ${colors.border.light}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                >
                    <FontAwesomeIcon icon={faUserCircle} style={{ color: colors.border.secondary, fontSize: "1.5rem" }} />
                </div>
            )}
            <span style={{ fontSize: "0.62rem", color: colors.text.muted, textAlign: "center", lineHeight: 1.2 }}>
                {label}
            </span>
        </div>
    );
}

const LIMIT = 10;

export default function VendorFailureStudentsModal({ show, onHide, vendorName, failureType, dateRange }: Props) {
    const { colors } = useTheme();
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lightbox, setLightbox] = useState<{ src: string; label: string } | null>(null);

    async function fetchStudents(p: number) {
        setLoading(true);
        const params = makeParams([
            { index: "vendorName", value: vendorName },
            { index: "failureType", value: failureType },
            { index: "start_time", value: moment(dateRange.startDate).toISOString() },
            { index: "end_time", value: moment(dateRange.endDate).toISOString() },
            { index: "page", value: String(p) },
            { index: "limit", value: String(LIMIT) },
        ]);
        await ClientService.getVendorFailureStudents(params)
            .then((res: any) => {
                const data = res.data?.data;
                setStudents(data?.records || []);
                setTotalPages(data?.pagination?.totalPages || 1);
                setTotalRecords(data?.pagination?.totalRecords || 0);
            })
            .catch((err: any) => console.log(err))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        if (!show) return;
        setPage(1);
        fetchStudents(1);
    }, [show, vendorName, failureType]);

    function goToPage(p: number) {
        setPage(p);
        fetchStudents(p);
    }

    const badgeColor = FAILURE_TYPE_COLORS[failureType] ?? "#9ca3af";

    const thStyle: React.CSSProperties = {
        padding: "10px 14px",
        fontSize: "0.68rem",
        fontWeight: 600,
        color: colors.text.muted,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        borderBottom: `1px solid ${colors.border.light}`,
        whiteSpace: "nowrap",
        background: colors.background.secondary,
    };

    const tdStyle: React.CSSProperties = {
        padding: "10px 14px",
        borderBottom: `1px solid ${colors.border.light}`,
        verticalAlign: "middle",
    };

    return (
        <>
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header
                closeButton
                style={{
                    background: colors.background.secondary,
                    borderBottom: `1px solid ${colors.border.light}`,
                    padding: "1rem 1.5rem",
                }}
            >
                <Modal.Title style={{ fontSize: "1rem" }}>
                    <div style={{ fontWeight: 700, color: colors.text.primary }}>
                        {vendorName}
                        <span
                            style={{
                                marginLeft: 10, fontSize: "0.75rem", fontWeight: 600,
                                color: badgeColor, background: `${badgeColor}18`,
                                borderRadius: "20px", padding: "2px 10px",
                            }}
                        >
                            {failureType}
                        </span>
                    </div>
                    <div style={{ fontSize: "0.75rem", color: colors.text.muted, fontWeight: 400, marginTop: 2 }}>
                        {totalRecords.toLocaleString()} students &middot;{" "}
                        {moment(dateRange.startDate).format("DD MMM")} – {moment(dateRange.endDate).format("DD MMM YYYY")}
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: 0, background: colors.background.primary }}>
                {loading ? (
                    <div className="d-flex align-items-center justify-content-center py-5">
                        <Spinner animation="border" style={{ color: colors.primary.main }} />
                    </div>
                ) : students.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center py-5">
                        <span style={{ color: colors.text.muted, fontSize: "0.9rem" }}>No students found</span>
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    {["#", "Photos", "Name", "Roll No", "District", "Facial", "Fingerprint"].map(h => (
                                        <th key={h} style={thStyle}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((s, i) => (
                                    <tr
                                        key={s._id}
                                        style={{
                                            background: i % 2 === 0 ? colors.background.primary : colors.background.secondary,
                                            transition: "background 0.15s",
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = colors.background.tertiary)}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? colors.background.primary : colors.background.secondary)}
                                    >
                                        {/* Row number */}
                                        <td style={{ ...tdStyle, color: colors.text.muted, fontSize: "0.75rem", width: 40 }}>
                                            {(page - 1) * LIMIT + i + 1}
                                        </td>

                                        {/* Three photo thumbnails */}
                                        <td style={{ ...tdStyle, width: 200 }}>
                                            <div className="d-flex gap-2">
                                                <PhotoCell src={s.avatar} label="Registration" onClick={() => s.avatar && setLightbox({ src: s.avatar, label: "Registration Photo" })} />
                                                <PhotoCell src={s.examDayPhoto} label="Exam Day" onClick={() => s.examDayPhoto && setLightbox({ src: s.examDayPhoto, label: "Exam Day Photo" })} />
                                                <PhotoCell src={s.reverificationPhoto} label="Reverification" onClick={() => s.reverificationPhoto && setLightbox({ src: s.reverificationPhoto, label: "Reverification Photo" })} />
                                            </div>
                                        </td>

                                        {/* Name */}
                                        <td style={{ ...tdStyle, fontWeight: 600, color: colors.text.primary, fontSize: "0.85rem" }}>
                                            {s.name}
                                        </td>

                                        {/* Roll No */}
                                        <td style={{ ...tdStyle, color: colors.text.secondary, fontSize: "0.82rem", fontFamily: "monospace" }}>
                                            {s.rollNo}
                                        </td>

                                        {/* District */}
                                        <td style={{ ...tdStyle, fontSize: "0.82rem", color: colors.text.secondary }}>
                                            <span className="d-flex align-items-center gap-1">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: colors.primary.main, fontSize: "0.7rem" }} />
                                                {s.city || "—"}
                                            </span>
                                        </td>

                                        {/* Facial status */}
                                        <td style={tdStyle}>
                                            <StatusPill status={s.facialStatus} />
                                        </td>

                                        {/* Fingerprint status */}
                                        <td style={tdStyle}>
                                            <StatusPill status={s.fingerprintStatus} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>

            {/* Pagination */}
            {totalPages > 1 && (
                <Modal.Footer
                    style={{
                        background: colors.background.secondary,
                        borderTop: `1px solid ${colors.border.light}`,
                        padding: "0.75rem 1.5rem",
                        justifyContent: "space-between",
                    }}
                >
                    <span style={{ fontSize: "0.78rem", color: colors.text.muted }}>
                        Page {page} of {totalPages} &middot; {totalRecords.toLocaleString()} total
                    </span>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-sm"
                            disabled={page <= 1 || loading}
                            onClick={() => goToPage(page - 1)}
                            style={{
                                border: `1px solid ${colors.border.light}`,
                                background: colors.background.primary,
                                color: colors.text.primary,
                                borderRadius: "8px", padding: "4px 10px",
                            }}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "0.75rem" }} />
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                            const p = start + i;
                            return (
                                <button
                                    key={p}
                                    className="btn btn-sm"
                                    onClick={() => goToPage(p)}
                                    style={{
                                        minWidth: 32,
                                        border: `1px solid ${p === page ? colors.primary.main : colors.border.light}`,
                                        background: p === page ? colors.primary.main : colors.background.primary,
                                        color: p === page ? "#fff" : colors.text.primary,
                                        borderRadius: "8px",
                                        fontWeight: p === page ? 600 : 400,
                                        fontSize: "0.78rem",
                                        padding: "4px 8px",
                                    }}
                                >
                                    {p}
                                </button>
                            );
                        })}

                        <button
                            className="btn btn-sm"
                            disabled={page >= totalPages || loading}
                            onClick={() => goToPage(page + 1)}
                            style={{
                                border: `1px solid ${colors.border.light}`,
                                background: colors.background.primary,
                                color: colors.text.primary,
                                borderRadius: "8px", padding: "4px 10px",
                            }}
                        >
                            <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "0.75rem" }} />
                        </button>
                    </div>
                </Modal.Footer>
            )}
        </Modal>

        {/* Lightbox */}
        <Modal
            show={!!lightbox}
            onHide={() => setLightbox(null)}
            centered
            size="sm"
            style={{ zIndex: 1060 }}
        >
            <Modal.Header
                closeButton
                style={{
                    background: colors.background.secondary,
                    borderBottom: `1px solid ${colors.border.light}`,
                    padding: "0.75rem 1rem",
                }}
            >
                <Modal.Title style={{ fontSize: "0.88rem", fontWeight: 600, color: colors.text.primary }}>
                    {lightbox?.label}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "1rem", background: "#000", textAlign: "center" }}>
                {lightbox && (
                    <img
                        src={lightbox.src}
                        alt={lightbox.label}
                        style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "8px" }}
                    />
                )}
            </Modal.Body>
        </Modal>
        </>
    );
}
